import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, Form, Button, Container, ListGroup, Badge } from "react-bootstrap";
import Loader from "../components/Loader";
import Message from "../components/Message";
import { useGetProductDetailsQuery, useCreateReviewMutation } from "../slices/productsApiSlice";
import { addToCart } from "../slices/cartSlice";
import { trackAddToCart, trackViewItem } from "../utils/analytics";
import ProductsItemCarousel from "../components/ProductItemCarousel";
import { toast } from "react-toastify";
import Meta from "../components/Meta";
import StayTop from "../components/StayTop";
import ProductDetails from "../components/ProductDetails";
import Rating from "../components/Rating";
import { useRecentlyViewed } from '../hooks/useRecentlyViewed';
import RecentlyViewed from '../components/RecentlyViewed';
import StickyNavFix from '../components/StickyNavFix';

const ProductScreen = () => {
    const { id: productId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [qty, setQty] = useState(1);
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState('');
    const [reviewImages, setReviewImages] = useState([]);

    // T-Shirt size state
    const [selectedSize, setSelectedSize] = useState('');
    const [availableStockForSize, setAvailableStockForSize] = useState(0);

    // Cap colour state
    const [selectedColour, setSelectedColour] = useState('');
    const [availableStockForColour, setAvailableStockForColour] = useState(0);

    const { data: product, refetch, isLoading, error } = useGetProductDetailsQuery(productId);
    const [createReview, { isLoading: loadingProductReview }] = useCreateReviewMutation();
    const { userInfo } = useSelector((state) => state.auth);

    // Initialize size for T-Shirts
    useEffect(() => {
        if (product && product.productType === 'Classic Tees' && product.sizeArray && product.sizeArray.length > 0) {
            const availableSize = product.sizeArray.find(size => size.tshirtInStock > 0);
            if (availableSize) {
                setSelectedSize(availableSize.size);
                setAvailableStockForSize(availableSize.tshirtInStock);
            } else {
                setSelectedSize('');
                setAvailableStockForSize(0);
            }
        }
    }, [product]);

    // Initialize colour for Hats
    useEffect(() => {
        if (product && product.productType === 'Hats' && product.colourArray && product.colourArray.length > 0) {
            const availableColour = product.colourArray.find(colour => colour.colourInStock > 0);
            if (availableColour) {
                setSelectedColour(availableColour.colour);
                setAvailableStockForColour(availableColour.colourInStock);
            } else {
                setSelectedColour('');
                setAvailableStockForColour(0);
            }
        }
    }, [product]);

    // Update stock when size changes
    useEffect(() => {
        if (selectedSize && product && product.sizeArray) {
            const sizeObj = product.sizeArray.find(s => s.size === selectedSize);
            if (sizeObj) {
                setAvailableStockForSize(sizeObj.tshirtInStock);
                setQty(1);
            } else {
                setAvailableStockForSize(0);
            }
        }
    }, [selectedSize, product]);

    // Update stock when colour changes
    useEffect(() => {
        if (selectedColour && product && product.colourArray) {
            const colourObj = product.colourArray.find(c => c.colour === selectedColour);
            if (colourObj) {
                setAvailableStockForColour(colourObj.colourInStock);
                setQty(1);
            } else {
                setAvailableStockForColour(0);
            }
        }
    }, [selectedColour, product]);

    const { addToRecentlyViewed, getRecentlyViewedExcluding } = useRecentlyViewed();

    useEffect(() => {
        if (product && product._id) {
            trackViewItem(product);
            addToRecentlyViewed({
                _id: product._id,
                name: product.name,
                image: product.image,
                price: product.price,
                rating: product.rating,
                numReviews: product.numReviews,
                isClearance: product.isClearance,
                productType: product.productType,
            });
        }
    }, [product?._id]);

    const addToCartHandler = () => {
        const cartItem = {
            ...product,
            qty,
            size: selectedSize,
            colour: selectedColour
        };
        dispatch(addToCart(cartItem));
        trackAddToCart(product, qty);
        navigate('/cart');
    };

    const submitHandler = async (e) => {
        e.preventDefault();
        try {
            await createReview({ productId, rating, comment, reviewImages }).unwrap();
            refetch();
            toast.success('Review submitted successfully');
            setRating(0);
            setComment('');
            setReviewImages([]);
        } catch (err) {
            toast.error(err?.data?.message || err.error);
        }
    };

    // Determine which stock value to pass to ProductDetails
    const isInStock = product?.productType === 'Classic Tees'
        ? availableStockForSize > 0
        : product?.productType === 'Hats'
            ? availableStockForColour > 0
            : product?.countInStock > 0;

    const setIsInStock = product?.productType === 'Classic Tees'
        ? setAvailableStockForSize
        : product?.productType === 'Hats'
            ? setAvailableStockForColour
            : () => {};

    return (
        <>
            <StickyNavFix />
            <StayTop />
            <Link className="btn btn-light my-3 orange-outline" onClick={() => navigate(-1)}>
                Go Back
            </Link>
            {isLoading ? (
                <Loader />
            ) : error ? (
                <Message variant='danger'>{error?.data?.message || error.error}</Message>
            ) : (
                <Container>
                    <h3>{product.name}</h3>
                    <Meta
                        title={product.name}
                        description={product.description}
                        keywords={[product.brand, product.character, product.productType, product.category]
                            .filter(Boolean)
                            .join(', ')}
                        image={product.image}
                        url={`https://retrodayz.co.uk/product/${product.slug || product._id}`}
                        product={product}
                    />
                    <Row className="flex-row-centered">
                        <Col xs={12} sm={12} md={12} lg={6} xl={7}>
                            <ProductsItemCarousel selectedColour={selectedColour} />
                        </Col>
                        <Col xs={12} sm={12} md={12} lg={6} xl={5} className="d-flex flex-column">
                            <div className="order-2 order-lg-1">
                                <ProductDescription product={product} />
                            </div>
                            <div className="order-1 order-lg-2">
                            <ProductDetails
                                product={product}
                                selectedSize={selectedSize}
                                setSelectedSize={setSelectedSize}
                                selectedColour={selectedColour}
                                setSelectedColour={setSelectedColour}
                                isInStock={isInStock}
                                setIsInStock={setIsInStock}
                                qty={qty}
                                setQty={setQty}
                                addToCartHandler={addToCartHandler}
                                productId={productId}
                                showDiscount={product.isClearance}
                            />
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TechnicalDetails product={product} />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <ProductReviews
                                product={product}
                                loadingProductReview={loadingProductReview}
                                userInfo={userInfo}
                                submitHandler={submitHandler}
                                rating={rating}
                                setRating={setRating}
                                comment={comment}
                                setComment={setComment}
                                reviewImages={reviewImages}
                                setReviewImages={setReviewImages}
                            />
                        </Col>
                    </Row>
                    <RecentlyViewed
                        products={getRecentlyViewedExcluding(productId)}
                    />
                </Container>
            )}
        </>
    );
};

const ProductDescription = ({ product }) => (
    <div className="product-description-large">
        <Card className="mt-3 mb-1">
            <Card.Body>
                <div className="product-description">
                    <h4>Product Description: </h4>
                    {product.description?.replace(/\\n/g, '\n').split('\n\n').map((paragraph, index) => (
                        <p key={index}>
                            {paragraph}
                        </p>
                    ))}
                </div>
            </Card.Body>
        </Card>
    </div>
);

const TechnicalDetails = ({ product }) => (
    <div className="product-description-small">
        <Card className="my-3">
            <Card.Body>
                <div className="product-description">
                    <h4>Technical Details: </h4>
                    {(product.productDetails || '').toString().replace(/\\n/g, '\n').split('\n').filter((line) => !/^technical details:?$/i.test(line.trim())).map((line, index) => (
                        <React.Fragment key={index}>
                            {line}<br />
                        </React.Fragment>
                    ))}
                </div>
            </Card.Body>
        </Card>
    </div>
);

const ProductReviews = ({
                            product,
                            loadingProductReview,
                            userInfo,
                            submitHandler,
                            rating,
                            setRating,
                            comment,
                            setComment,
                            reviewImages,
                            setReviewImages
                        }) => {
    const [uploading, setUploading] = useState(false);

    const uploadFileHandler = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        if (reviewImages.length + files.length > 3) {
            toast.error('Maximum 3 images allowed per review');
            return;
        }

        setUploading(true);

        try {
            const uploadPromises = Array.from(files).map(async (file) => {
                const formData = new FormData();
                formData.append('reviewImage', file);

                const res = await fetch('/api/upload/review', {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${userInfo.token}`,
                    },
                    body: formData,
                });

                if (!res.ok) {
                    throw new Error('Upload failed');
                }

                const data = await res.json();
                return data.imageUrl;
            });

            const uploadedUrls = await Promise.all(uploadPromises);
            setReviewImages([...reviewImages, ...uploadedUrls]);
            toast.success('Images uploaded successfully');
        } catch (error) {
            toast.error('Failed to upload images');
            console.error(error);
        } finally {
            setUploading(false);
        }
    };

    const removeImage = (indexToRemove) => {
        setReviewImages(reviewImages.filter((_, index) => index !== indexToRemove));
    };

    return (
        <Card className="my-3">
            <Card.Body>
                <div className="product-reviews mt-3">
                    <h2>Reviews</h2>
                    {product.reviews.length === 0 && <Message>No Reviews</Message>}
                    <ListGroup variant='flush'>
                        {product.reviews.map((review) => (
                            <ListGroup.Item key={review._id}>
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <strong>{review.name}</strong>
                                        {review.verified && (
                                            <Badge
                                                bg="success"
                                                className="ms-2"
                                                style={{ fontSize: '0.75rem' }}
                                            >
                                                ✓ Verified Purchase
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                                <Rating value={review.rating} />
                                <p className="text-muted">{review.createdAt.substring(0, 10)}</p>
                                <p>{review.comment}</p>

                                {review.reviewImages && review.reviewImages.length > 0 && (
                                    <div className="review-images mt-2" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                        {review.reviewImages.map((imageUrl, index) => (
                                            <img
                                                key={index}
                                                src={imageUrl}
                                                alt={`Review ${index + 1}`}
                                                style={{
                                                    width: '100px',
                                                    height: '100px',
                                                    objectFit: 'cover',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    border: '1px solid #ddd'
                                                }}
                                                onClick={() => window.open(imageUrl, '_blank')}
                                            />
                                        ))}
                                    </div>
                                )}
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                    <h2>Write a Customer Review</h2>
                    {loadingProductReview && <Loader />}
                    {userInfo ? (
                        <Form onSubmit={submitHandler}>
                            <Form.Group controlId='rating'>
                                <Form.Label>Rating</Form.Label>
                                <Form.Control
                                    as='select'
                                    value={rating}
                                    onChange={(e) => setRating(e.target.value)}
                                >
                                    <option value="5">⭐⭐⭐⭐⭐ Excellent</option>
                                    <option value="4">⭐⭐⭐⭐ Good</option>
                                    <option value="3">⭐⭐⭐ Average</option>
                                    <option value="2">⭐⭐ Poor</option>
                                    <option value="1">⭐ Terrible</option>
                                </Form.Control>
                            </Form.Group>
                            <Form.Group controlId='comment' className="mt-2">
                                <Form.Label>Comment</Form.Label>
                                <Form.Control
                                    as='textarea'
                                    rows='3'
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                />
                            </Form.Group>

                            <Form.Group controlId='reviewImages' className="mt-2">
                                <Form.Label>Add Photos (Optional - Max 3)</Form.Label>
                                <Form.Control
                                    type='file'
                                    accept='image/*'
                                    multiple
                                    onChange={uploadFileHandler}
                                    disabled={uploading || reviewImages.length >= 3}
                                />
                                <Form.Text className="text-muted">
                                    Upload photos of your purchase (JPG, PNG, WebP - Max 5MB each)
                                </Form.Text>
                            </Form.Group>

                            {reviewImages.length > 0 && (
                                <div className="mt-2" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                    {reviewImages.map((url, index) => (
                                        <div key={index} style={{ position: 'relative' }}>
                                            <img
                                                src={url}
                                                alt={`Preview ${index + 1}`}
                                                style={{
                                                    width: '100px',
                                                    height: '100px',
                                                    objectFit: 'cover',
                                                    borderRadius: '8px',
                                                    border: '1px solid #ddd'
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeImage(index)}
                                                style={{
                                                    position: 'absolute',
                                                    top: '-8px',
                                                    right: '-8px',
                                                    background: 'red',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '50%',
                                                    width: '24px',
                                                    height: '24px',
                                                    cursor: 'pointer',
                                                    fontSize: '14px',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {uploading && <Loader />}

                            <Button
                                className="mt-2 blue-outline"
                                disabled={loadingProductReview || uploading}
                                type='submit'
                            >
                                Submit
                            </Button>
                        </Form>
                    ) : (
                        <Message>Please <Link to='/login'>sign in</Link> to write a review</Message>
                    )}
                </div>
            </Card.Body>
        </Card>
    );
};

export default ProductScreen;